import { selectLicenseProductState } from '../../../reducers/SmartDentReducer';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import { computeDealerRemaining, MAX_QR_PER_REQUEST } from '../../../lib/smartDentQuota';
// SEC-055 PR #546 리뷰: 종전에는 `setError(result?.reason)` 로 영문 사유 코드가 사용자에게
// 그대로 노출됐다(할당 모달에만 문구 맵이 있던 비대칭). 맵을 공유해 문구로 표시한다.
import { ISSUANCE_ERROR_MESSAGES, resolveLicenseErrorMessage } from '../../../lib/licenseErrorMessages';
import { Translate as t } from '../../../config/Translate';
import VoucherPrintArea from '../../../components/voucher/VoucherPrintArea';
import VoucherPreviewCard from '../../../components/voucher/VoucherPreviewCard';
import { resolveRedeemQrpath, buildQrValue as buildVoucherQrValue } from '../../../lib/voucherRedeem';
import { useLicenseProduct } from '../../../hooks/useLicenseProduct';

// PR #187 리뷰 Major #3 — `corpId` 가 정식 prop. `managerGroupId` 는 deprecated alias
// 로 한 cycle 유지 (caller 점진 이전, 다음 IMP 에서 제거).
const VoucherPrintModal = ({
    show,
    mode,
    packageId,
    corpId,
    /** @deprecated `corpId` 사용. */
    managerGroupId,
    dealerId,
    existingQrs,
    onClose,
    onIssued,
    dealerAllocation,
    voucherTemplate,
    selectedPackage,
    companyMap = {},
    // IMP-422 §12.1-B: 법인 자기 선택(selfUsable) 등 dealerAllocation 과 다른 잔여 소스를
    // 상위(LicenseDealerTab)가 override 하기 위한 optional prop. 미전달 시 기존 동작 유지.
    remainingOverride,
    // FEAT-059: 제품(optional). 생략 = SMARTDent.
    product: productProp,
}) => {
    const product = useLicenseProduct(productProp);
    // FEAT-022: rayteams-product-package.qrpath 가 있으면 QR 값을 full URL 로 인코딩.
    // 형식: `${qrpath}=${type}-${token}` (예: https://raydevelop.com/redeem?code=initial-XXXX).
    // qrpath 부재 시 raw token (legacy 호환).
    // FEAT-049: 발급 대상 조직(dealerId)의 국가가 'RU' 이면 RU 전용 redeem base 사용.
    const qrpath = resolveRedeemQrpath(selectedPackage?.qrpath, dealerId, companyMap, product);
    const buildQrValue = (qr) => buildVoucherQrValue(qr, qrpath);
    const effectiveCorpId = corpId ?? managerGroupId;
    const dispatch = useDispatch();
    const [qrType, setQrType] = useState('initial');
    const [count, setCount] = useState(1);
    const [issuedQrs, setIssuedQrs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [error, setError] = useState(null);
    // PR #183 Round 2 FYI — Safari fallback setTimeout id 보관.
    // 3초 내 모달 close 로 unmount 되면 restore() 가 setState 호출하여 React 17
    // "state update on unmounted component" 경고가 뜨므로 cleanup 에서 clear.
    const safariFallbackTimerRef = useRef(null);
    // PR #183 Round 3 FYI — afterprint 리스너도 unmount cleanup 에서 제거하기 위한 ref.
    // timer clear 후 unmount, 그 뒤 afterprint fire 레이스에서 setIsPrinting 경고 차단.
    const restoreHandlerRef = useRef(null);

    // 라벨은 값을 고르는 분기와 같은 조건: override(법인 selfUsable) 이면 Self-Usable,
    // 아니면 딜러 레코드 t−u = Redeemable Remainder(용어집 §2).
    const useOverride = remainingOverride != null;
    const remaining = useOverride ? remainingOverride : computeDealerRemaining(dealerAllocation);
    const remainingLabelKey = useOverride ? 'license.label.selfUsable' : 'license.label.redeemableRemainder';

    useEffect(() => {
        if (show) {
            setQrType('initial');
            setCount(1);
            setError(null);
            if (mode === 'reprint' && existingQrs) {
                setIssuedQrs(existingQrs);
            } else {
                setIssuedQrs([]);
            }
            setIsPrinting(false);
        }
    }, [show, mode, existingQrs]);

    const handleIssue = async () => {
        setError(null);
        setLoading(true);
        try {
            const now = new Date();
            const lastDayOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0).getDate();
            const validuntil = new Date(now.getFullYear(), now.getMonth() + 1, Math.min(now.getDate(), lastDayOfNextMonth)).getTime();
            const result = await dispatch(SmartDentAction.IssueQr({
                packageId,
                type: qrType,
                count,
                corpId: effectiveCorpId,
                dealerId,
                validuntil,
                product,
            }));
            if (!result || result?.ok === false) {
                setError(resolveLicenseErrorMessage(
                    ISSUANCE_ERROR_MESSAGES,
                    result?.reason,
                    result?.issuable,
                    t,
                    t('license.message.error.issuanceFailed'),
                ));
            } else if (result?.ok === true) {
                setIssuedQrs(result.qrRecords ?? []);
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = useCallback(() => {
        const prevTitle = document.title;
        document.title = `voucher-print-${Date.now()}`;
        setIsPrinting(true);

        let done = false;
        const restore = () => {
            if (done) return;
            done = true;
            document.title = prevTitle;
            setIsPrinting(false);
            window.removeEventListener('afterprint', restore);
            restoreHandlerRef.current = null;
            if (safariFallbackTimerRef.current) {
                clearTimeout(safariFallbackTimerRef.current);
                safariFallbackTimerRef.current = null;
            }
        };
        // ref 로 올려 unmount cleanup 에서도 동일 참조로 removeEventListener 가능.
        restoreHandlerRef.current = restore;
        window.addEventListener('afterprint', restore);
        // Safari fallback — afterprint 미발사 브라우저 대비 (UX 복구 보장).
        // timer id 를 ref 에 보관하여 unmount cleanup 에서 clear 가능하게 함.
        safariFallbackTimerRef.current = setTimeout(restore, 3000);

        // 배경 이미지 preload 후 인쇄 — 네트워크 fetch 가 print() 보다 늦어 이미지 누락
        // 되는 회귀 방지. 이미지 부재(synthetic/cascade 미적용) 또는 로드 실패 시도 즉시 인쇄.
        const bgUrl = voucherTemplate?.backgroundImageUrl;
        if (bgUrl) {
            const img = new Image();
            const fire = () => {
                // requestAnimationFrame 으로 다음 paint 까지 대기 — DOM 업데이트(setIsPrinting)
                // 가 적용된 후 print() 실행 보장
                requestAnimationFrame(() => window.print());
            };
            img.onload = fire;
            img.onerror = fire;
            img.src = bgUrl;
        } else {
            requestAnimationFrame(() => window.print());
        }
    }, [voucherTemplate]);

    // Unmount 시 남아있는 setTimeout 정리 — 인쇄 직후 모달 닫힘 등에서 setState 경고 방지.
    // afterprint 리스너도 함께 제거 (Round 3 FYI): timer clear 후 unmount, 그 뒤
    // afterprint fire 레이스에서 unmounted 컴포넌트 setState 경고 원천 차단.
    useEffect(() => () => {
        if (safariFallbackTimerRef.current) {
            clearTimeout(safariFallbackTimerRef.current);
            safariFallbackTimerRef.current = null;
        }
        if (restoreHandlerRef.current) {
            window.removeEventListener('afterprint', restoreHandlerRef.current);
            restoreHandlerRef.current = null;
        }
    }, []);

    const handleClose = () => {
        if (mode === 'new' && issuedQrs.length > 0) {
            onIssued?.();
        }
        onClose?.();
    };

    const remainingForType = qrType === 'initial'
        ? Math.max(0, remaining.newQty)
        : Math.max(0, remaining.renewQty);
    const canIssueSelected = count >= 1 && count <= remainingForType;

    return (
        <>
            {/* 인쇄 전용 영역 — FEAT-023: 법인 템플릿 기반 페이지 단위 출력.
                qrpath 있으면 각 voucher 에 qrUrl 주입 → VoucherPrintArea 가 그것을 우선 사용. */}
            {isPrinting && (
                <VoucherPrintArea
                    vouchers={issuedQrs.map((qr) => ({ ...qr, qrUrl: buildQrValue(qr) }))}
                    template={voucherTemplate}
                />
            )}

            <Modal show={show && !isPrinting} onHide={handleClose} size="lg">
                <Modal.Header className="d-flex justify-content-between align-items-center">
                    <h3 className="modal-title mb-0 fs-3">
                        {mode === 'reprint' ? t('license.button.reprintVoucher') : t('license.button.issueVoucher')}
                    </h3>
                    <button
                        type="button"
                        className="btn-close"
                        aria-label={t('common.button.close')}
                        onClick={handleClose}
                    />
                </Modal.Header>
                <Modal.Body className="p-3">
                    {error && (
                        <div className="alert alert-danger py-2">{error}</div>
                    )}

                    {/* mode=new: 발급 입력 */}
                    {mode === 'new' && issuedQrs.length === 0 && (
                        <>
                            {/* Type별 잔여 개수 표시 */}
                            <div className="mb-3 p-2 bg-light rounded">
                                <div className="small text-muted">{t(remainingLabelKey)}</div>
                                <div>
                                    <strong>{t('license.label.initial')}</strong>: {Math.max(0, remaining.newQty)}
                                    {' / '}
                                    <strong>{t('license.label.renewal')}</strong>: {Math.max(0, remaining.renewQty)}
                                </div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">{t('common.field.type')}</label>
                                <select
                                    className="form-select"
                                    value={qrType}
                                    onChange={(e) => { setQrType(e.target.value); setCount(1); }}
                                >
                                    <option value="initial">{t('license.label.initial')}</option>
                                    <option value="renewal">{t('license.label.renewal')}</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">{t('common.label.count')}</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={1}
                                    max={Math.min(remainingForType, MAX_QR_PER_REQUEST)}
                                    value={count}
                                    onChange={(e) => {
                                        const v = parseInt(e.target.value, 10);
                                        const max = Math.min(remainingForType, MAX_QR_PER_REQUEST);
                                        setCount(Number.isNaN(v) ? 1 : Math.min(Math.max(1, v), max));
                                    }}
                                />
                            </div>
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleIssue}
                                disabled={loading || canIssueSelected === false}
                            >
                                {loading ? t('license.status.issuing') : t('license.button.issue')}
                            </button>
                        </>
                    )}

                    {/* QR 그리드 */}
                    {issuedQrs.length > 0 && (
                        <>
                            {!voucherTemplate?.backgroundImageUrl && (
                                <div className="alert alert-warning py-2 small mb-3">
                                    {t('license.message.warning.voucherTemplateNotLoaded')}
                                </div>
                            )}
                            <div className="row g-2 mb-3 justify-content-center">
                                {issuedQrs.map((qr) => (
                                    <div key={qr.sk || qr._id} className="col-6">
                                        <VoucherPreviewCard qr={qr} qrValue={buildQrValue(qr)} />
                                    </div>
                                ))}
                            </div>
                            <button
                                className="btn btn-success w-100"
                                onClick={handlePrint}
                            >
                                {t('common.button.print')}
                            </button>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

const mapState = (state, ownProps) => {
    const licenseState = selectLicenseProductState(state, ownProps?.product?.key);
    return {
        dealerAllocation: licenseState.dealerAllocation,
        voucherTemplate: licenseState.voucherTemplate,
        selectedPackage: licenseState.selectedPackage,
    };
};

export default connect(mapState)(VoucherPrintModal);
